import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

import config from '../../config';
import dayjs from 'dayjs';

import '../../styles/popup.css';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';

import { inputBaseClasses } from '@mui/material/InputBase';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const AddPropertyForm = ({ addressId, onNext, setSubmitHandler }) => {
    const [formData, setFormData] = useState({
        name: '',
        address: addressId || '',
        type: '',
        price: '',
        lotArea: '',
        floorArea: '',
        amenities: [],
        developer: '',
        description: '',
        availableFrom: null, // Initialize to null because of DatePicker
        isAvailable: true,
    });
    const [amenityInput, setAmenityInput] = useState('');
    const [error, setError] = useState('');
    const [existingPropertyId, setExistingPropertyId] = useState('');

    useEffect(() => {
        if (addressId) {
            setFormData((prevData) => ({
                ...prevData,
                address: addressId,
            }));
        }
    }, [addressId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: name === 'isAvailable' ? e.target.checked : value,
        }));
    };

    const handleAddAmenity = () => {
        if (amenityInput.trim()) {
            setFormData((prevData) => ({
                ...prevData,
                amenities: [...prevData.amenities, amenityInput.trim()],
            }));
            setAmenityInput('');
        }
    };

    const handleRemoveAmenity = (index) => {
        setFormData((prevData) => ({
            ...prevData,
            amenities: prevData.amenities.filter((_, i) => i !== index),
        }));
    };

    const formDataRef = useRef(formData);
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    const handleSubmit = useCallback(async () => {
        setError('');
        setExistingPropertyId('');

        try {
            const response = await fetch(`${config.API_URL}/v2/property`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(formDataRef.current),
            });

            const data = await response.json();
            console.log("API Response:", data);

            if (response.status === 409) {
                setError(data.message || 'Property already exists.');
                setExistingPropertyId(data._id);
                return false;
            } else if (!response.ok) {
                toast.error('Failed to create property. Please try again.');
                setError(data.error || 'Error creating property.');
                return false;
            } else {
                toast.success('Property created successfully!');
                onNext(data._id);
                return true;
            }
        } catch (err) {
            console.log("Fetch Error:", err);
            setError('An unexpected error occurred.');
            return false;
        }
    }, [onNext]);

    useEffect(() => {
        if (setSubmitHandler) setSubmitHandler(() => handleSubmit);
    }, [setSubmitHandler, handleSubmit]);

    const handleUseExisting = () => {
        if (existingPropertyId) {
            onNext(existingPropertyId); // Proceed with the existing property ID
        }
    };

    return (
        <>
            {existingPropertyId && (
                <div className="existing-property">
                    <p>
                        A property with this address already exists. Do you want to use the existing property?
                    </p>
                    <button
                        type="button"
                        className="use-existing-button"
                        onClick={handleUseExisting}
                    >
                        Use Existing Property
                    </button>
                </div>
            )}
            <TextField
                label="Property Name"
                name="name"
                variant="outlined"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                sx={{ marginBottom: '1rem' }}
                required
            />
            <div className="popup-columns">
                <TextField
                    label="Developer"
                    name="developer"
                    variant="outlined"
                    fullWidth
                    value={formData.developer}
                    onChange={handleChange}
                    sx={{ marginBottom: '1rem' }}
                />
                <Autocomplete
                    name="type"
                    value={formData.type ? formData.type.charAt(0).toUpperCase() + formData.type.slice(1) : ''}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            setFormData((prevData) => ({
                                ...prevData,
                                type: newValue.toLowerCase(), // Store as lowercase
                            }));
                        }
                    }}
                    options={['house', 'apartment', 'commercial', 'condo']}
                    sx={{ width: '100%', marginBottom: '1rem' }}
                    renderOption={(props, option) => (
                        <li {...props}>
                            {option.charAt(0).toUpperCase() + option.slice(1)} {/* Capitalized in dropdown */}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Type"
                            variant="outlined"
                            fullWidth
                            required
                        />
                    )}
                />
            </div>
            <div className="popup-columns">
                <TextField
                    label="Lot Area"
                    name="lotArea"
                    variant="outlined"
                    value={formData.lotArea}
                    onChange={handleChange}
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment
                                    position="end"
                                    sx={{
                                        opacity: 0,
                                        pointerEvents: 'none',
                                        [`[data-shrink=true] ~ .${inputBaseClasses.root} > &`]: {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    m²
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{ width: '100%', marginBottom: '1rem' }}
                />
                <TextField
                    label="Floor Area"
                    name="floorArea"
                    variant="outlined"
                    value={formData.floorArea}
                    onChange={handleChange}
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment
                                    position="end"
                                    sx={{
                                        opacity: 0,
                                        pointerEvents: 'none',
                                        [`[data-shrink=true] ~ .${inputBaseClasses.root} > &`]: {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    m²
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{ width: '100%', marginBottom: '1rem' }}
                />
            </div>

            <div className="amenities-section">
                <div className="amenities-input">
                    <TextField
                        label="Add Amenity"
                        value={amenityInput}
                        fullWidth
                        onChange={(e) => setAmenityInput(e.target.value)}
                    />
                    <button id="amenity-button" className="button" type="button" onClick={handleAddAmenity}>
                        <i className="fas fa-plus"></i>
                    </button>
                </div>
                <ul style={{ marginBottom: '1rem', fontSize: '14px' }}>
                    {formData.amenities.map((amenity, index) => (
                        <li key={index}>
                            {amenity}{' '}
                            <button id="delete-amenity-button" type="button" onClick={() => handleRemoveAmenity(index)}>
                                <i className="fas fa-trash"></i>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
            <TextField
                label="Description"
                name="description"
                multiline
                fullWidth
                rows={2}
                value={formData.description}
                onChange={handleChange}
                sx={{ marginBottom: '1rem' }}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    label="Available From"
                    value={formData.availableFrom ? dayjs(formData.availableFrom) : null}
                    onChange={(newValue) =>
                        setFormData((prevData) => ({
                            ...prevData,
                            availableFrom: newValue ? newValue.toISOString() : '',
                        }))
                    }
                    sx={{ width: '100%', marginBottom: '1rem' }}
                />
            </LocalizationProvider>
            {error && <p className="error-text w-full font-poppins text-center text-[#FF0000]">{error}</p>}
        </>
    );
};

export default AddPropertyForm;
